import {
  ArrowLeft,
  Bot,
  ChevronDown,
  History,
  Loader2,
  MessageCircle,
  MessageCirclePlus,
  MessageCircleX,
  Minus,
  MoreHorizontal,
  Paperclip,
  Plus,
  Send,
  X
} from 'lucide-react';
import DOMPurify from 'dompurify';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import stradaLogo from '../../assets/strada-logo.svg';
import { widgetKeys } from '../api/keys';
import { useEndChat } from '../api/useEndChat';
import { useGetChat } from '../api/useGetChat';
import { useGetHistory } from '../api/useGetHistory';
import { useGetTheme } from '../api/useGetTheme';
import { useSendMessage } from '../api/useSendMessage';
import { useStartChat } from '../api/useStartChat';
import { useUpdateCsatRating } from '../api/useUpdateCsatRating';
import { useChatWebSocket } from '../hooks/useChatWebSocket';
import { useContextBasedChat } from '../hooks/useContextBasedChat';
import { useKeyboardHeight } from '../hooks/useKeyboardHeight';
import { useSafeAreaAndKeyboardPadding } from '../hooks/useSafeAreaAndKeyboardPadding';
import { useUserToken } from '../hooks/useUserToken';
import type { InteractiveComponent } from 'fe-be-utils';
import { INTERACTIVE_TOOL_NAMES } from 'fe-be-utils';
import { cn } from '../lib/utils';
import { sdkBridge } from '../sdk-bridge';
import { mergeTheme, resolveThemeForDarkMode, type WidgetTheme } from '../types/theme';
import { getBackgroundStyle } from '../utils/theme';
import { ChatConversation } from './ChatConversation';
import { InteractiveComponentRenderer } from './interactive/InteractiveComponentRenderer';
import { Button } from './ui/button';
import { Drawer, DrawerClose, DrawerContent, DrawerFooter, DrawerHeader, DrawerTitle } from './ui/drawer';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { TypingIndicator } from './ui/typing-indicator';

type UserOption = {
  value: string;
  text: string;
};

type ChatInterfaceProps = {
  organizationId: string;
  agentId: string;
  chatId: string;
  isStartingChat?: boolean;
  userToken: string | null;
  theme: WidgetTheme;
  isReactNative: boolean;
  safeAreaInsets?: {
    top: number;
    bottom: number;
    left: number;
    right: number;
  };
  defaultView: 'chat' | 'list';
  metadata: Record<string, unknown>;
  useCustomParent?: boolean;
  darkMode?: boolean;
  onNewChat: (clearStorage?: boolean) => void;
  onViewList: () => void;
  onMinimize: () => void;
  externalKeyboardHeight?: number;
};

const ChatInterface = ({
  organizationId,
  agentId,
  chatId,
  isStartingChat,
  userToken,
  theme,
  isReactNative,
  safeAreaInsets,
  onNewChat,
  onViewList,
  onMinimize,
  externalKeyboardHeight,
  useCustomParent = false,
  darkMode = false
}: ChatInterfaceProps) => {
  const [inputValue, setInputValue] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState<string>();
  const [endChatDrawerOpen, setEndChatDrawerOpen] = useState<'newChat' | 'endChat' | null>(null);
  const [componentDismissed, setComponentDismissed] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const previousPaddingRef = useRef(0);
  const [optionsScrolledToBottom, setOptionsScrolledToBottom] = useState(true);

  const { mutateAsync: sendMessage, isPending } = useSendMessage(chatId, organizationId);
  const { mutateAsync: endChat } = useEndChat(chatId);
  const { data: existingChat, isLoading: chatLoading, isError } = useGetChat(chatId, organizationId, agentId);
  const { mutateAsync: updateCsatRating } = useUpdateCsatRating(chatId);

  // Resolve theme colors based on dark mode
  const resolvedTheme = useMemo(() => resolveThemeForDarkMode(theme, darkMode), [theme, darkMode]);

  const chat = existingChat?.chat;
  const isChatEnded = useMemo(
    () => !!(chat?.terminatedAt || chat?.abandonedAt || chat?.handoffClosedAt),
    [chat?.terminatedAt, chat?.abandonedAt, chat?.handoffClosedAt]
  );
  const messages = useMemo(() => chat?.transcript?.messages || [], [chat?.transcript?.messages]);

  // Derive active interactive component from the last message in the transcript.
  // When the last message is a ui_* tool call, the component is active.
  const derivedActiveComponent = useMemo((): InteractiveComponent | null => {
    if (messages.length === 0) return null;
    const lastMessage = messages[messages.length - 1];
    if (lastMessage.role !== 'tool' || !('toolName' in lastMessage)) return null;
    const componentType = INTERACTIVE_TOOL_NAMES[lastMessage.toolName as keyof typeof INTERACTIVE_TOOL_NAMES];
    if (!componentType) return null;
    let params = {};
    if (lastMessage.toolArguments) {
      try {
        params = JSON.parse(lastMessage.toolArguments);
      } catch {
        return null;
      }
    }
    return { type: componentType, params } as InteractiveComponent;
  }, [messages]);

  const activeComponentKey = derivedActiveComponent
    ? `${derivedActiveComponent.type}:${JSON.stringify(derivedActiveComponent.params)}`
    : null;

  useEffect(() => {
    setComponentDismissed(false);
  }, [activeComponentKey]);

  const activeComponent = componentDismissed ? null : derivedActiveComponent;

  useChatWebSocket(organizationId, agentId, chatId);

  // Use custom hook for keyboard height detection (includes system resize detection)
  const { effectiveHeight, isSystemResizing } = useKeyboardHeight(isReactNative, externalKeyboardHeight);

  // Centralized padding logic: handles safe area + keyboard + Android detection
  const { bottom: paddingBottom } = useSafeAreaAndKeyboardPadding(
    isReactNative,
    effectiveHeight,
    isSystemResizing,
    safeAreaInsets,
    typeof window !== 'undefined' ? window.screen.height : null
  );

  // Adjust scroll when padding changes
  useEffect(() => {
    const paddingDelta = paddingBottom - previousPaddingRef.current;
    if (paddingDelta !== 0 && scrollRef.current) {
      scrollRef.current.scrollTop += paddingDelta;
      previousPaddingRef.current = paddingBottom;
    }
  }, [paddingBottom]);

  useEffect(() => {
    if (isError && chatId) {
      onNewChat();
    }
  }, [isError, chatId, onNewChat]);

  // Auto-start new chat if current chat is abandoned
  // Clear storage to ensure the abandoned chat is replaced
  useEffect(() => {
    if (chat?.abandonedAt) {
      onNewChat(true); // Pass true to clear storage
    }
  }, [chat?.abandonedAt, onNewChat]);

  const parseUserOptions = (
    content: string | Array<unknown> | null | undefined
  ): { options: UserOption[]; isStrict: boolean } => {
    if (!content || typeof content !== 'string') {
      return { options: [], isStrict: false };
    }
    const isStrict = content.includes('<options_for_user_to_choose_strict>');
    const optionsRegex = isStrict
      ? /<options_for_user_to_choose_strict>([\s\S]*?)<\/options_for_user_to_choose_strict>/g
      : /<options_for_user_to_choose>([\s\S]*?)<\/options_for_user_to_choose>/g;
    const optionRegex = /<option(?:\s+value="([^"]+)")?>([^<]+)<\/option>/g;
    const options: UserOption[] = [];
    let match;
    while ((match = optionsRegex.exec(content)) !== null) {
      const optionsContent = match[1];
      let optionMatch;
      while ((optionMatch = optionRegex.exec(optionsContent)) !== null) {
        const optionValue = optionMatch[1] ? optionMatch[1].trim() : optionMatch[2].trim();
        const optionText = optionMatch[2].trim();
        options.push({
          value: optionValue,
          text: optionText
        });
      }
    }
    return { options, isStrict };
  };

  const { userOptions, isStrictMode } = useMemo(() => {
    if (messages.length > 0) {
      const lastAssistantMessage = [...messages].reverse().find((msg) => msg.role === 'bot');
      if (lastAssistantMessage && 'content' in lastAssistantMessage) {
        const { options, isStrict } = parseUserOptions(lastAssistantMessage.content);
        return { userOptions: options, isStrictMode: isStrict };
      }
    }
    return { userOptions: [], isStrictMode: false };
  }, [messages]);

  const removeUserOptions = (content: string | Array<unknown> | null | undefined): string => {
    if (!content || typeof content !== 'string') return '';
    return content
      .replace(/<options_for_user_to_choose>[\s\S]*?<\/options_for_user_to_choose>/g, '')
      .replace(/<options_for_user_to_choose_strict>[\s\S]*?<\/options_for_user_to_choose_strict>/g, '')
      .trim();
  };

  const handleOptionSelect = async (option: UserOption) => {
    if (isPending) return;
    if (option.value === 'end_chat') {
      await endChat();
      return;
    }
    setInputValue('');
    await sendMessage({ message: option.text, userToken });

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
    });
  };

  const MAX_MESSAGE_LENGTH = 300;

  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const validFiles = files.filter((file) => file.size <= MAX_FILE_SIZE);
    if (validFiles.length < files.length) {
      setFileError('File too large');
      setTimeout(() => setFileError(undefined), 3000);
    }
    setSelectedFiles((prev) => [...prev, ...validFiles].slice(0, 10));
    // Reset input so the same file can be selected again
    e.target.value = '';
  };

  const removeFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const hasText = inputValue.trim().length > 0;
    const hasFiles = selectedFiles.length > 0;
    if ((!hasText && !hasFiles) || isPending || (hasText && inputValue.length > MAX_MESSAGE_LENGTH)) return;

    // If an interactive component was dismissed, route through component submit
    if (derivedActiveComponent && componentDismissed && hasText) {
      setInputValue('');
      await handleComponentSubmit(inputValue.trim());
      return;
    }

    const filesToSend = [...selectedFiles];
    setInputValue('');
    setSelectedFiles([]);

    await sendMessage({
      message: inputValue,
      userToken,
      files: filesToSend.length > 0 ? filesToSend : undefined
    });
  };

  const handleComponentSubmit = async (value: string) => {
    await sendMessage({ message: value, userToken });
  };

  const handleStartNewChat = useCallback(() => {
    // Count user messages
    const userMessageCount = messages.filter((msg) => msg.role === 'user').length;

    // If more than 1 user message, show confirmation drawer
    if (userMessageCount >= 1) {
      setEndChatDrawerOpen('newChat');
    } else {
      // Otherwise, start new chat directly
      onNewChat();
    }
  }, [messages, onNewChat]);

  const handleEndChat = useCallback(async () => {
    const userMessageCount = messages.filter((msg) => msg.role === 'user').length;

    if (userMessageCount >= 1) {
      setEndChatDrawerOpen('endChat');
    } else {
      await endChat();
    }
  }, [messages, endChat]);

  const confirmEndChat = useCallback(async () => {
    await endChat();
    setEndChatDrawerOpen(null);

    if (endChatDrawerOpen === 'newChat') {
      onNewChat();
    }
  }, [endChat, endChatDrawerOpen, onNewChat]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, userOptions, activeComponent]);

  useEffect(() => {
    if (isChatEnded) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [isChatEnded]);

  const optionsScrollContainerRef = useCallback((el: HTMLDivElement | null) => {
    if (!el) return;
    // Check whether content overflows on mount — fires fresh each time options appear
    setOptionsScrolledToBottom(el.scrollHeight - el.clientHeight < 2);
  }, []);

  // Scroll when widget is opened (listen for SDK event)
  useEffect(() => {
    const handleWidgetOpened = () => {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 250);
    };

    sdkBridge.on('STRADA_WIDGET_OPENED', handleWidgetOpened);

    return () => {
      sdkBridge.off('STRADA_WIDGET_OPENED', handleWidgetOpened);
    };
  }, []);

  const OPTION_ROW_HEIGHT_PX = 34;
  const OPTIONS_GAP_PX = 8;
  // In non-strict mode show 2 fewer to account for input field
  const maxVisibleOptions = isStrictMode ? theme.maxVisibleOptions : Math.max(theme.maxVisibleOptions - 2, 1);
  const needsScroll = userOptions.length > maxVisibleOptions;
  // Only constrain height when options overflow — avoids phantom scroll space when all options fit
  const optionsMaxHeight = needsScroll
    ? Math.max(0, maxVisibleOptions * (OPTION_ROW_HEIGHT_PX + OPTIONS_GAP_PX) - OPTIONS_GAP_PX)
    : undefined;

  return (
    <div
      className="chat-widget-root flex flex-col h-full"
      data-keyboard-height={effectiveHeight}
      style={{
        paddingBottom: paddingBottom
      }}
    >
      <div
        className={cn(
          'flex items-center justify-between sm:rounded-t-2xl flex-shrink-0 relative',
          !isReactNative && 'p-4'
        )}
        style={{
          ...getBackgroundStyle(resolvedTheme.headerBackgroundColor),
          color: resolvedTheme.headerTextColor,
          ...(isReactNative && safeAreaInsets
            ? {
                marginTop: -safeAreaInsets.top,
                paddingTop: safeAreaInsets.top,
                paddingBottom: 8,
                paddingLeft: 16,
                paddingRight: 16
              }
            : {})
        }}
      >
        {isReactNative ? (
          <>
            {/* React Native: Back button on left (hidden when useCustomParent) */}
            {!useCustomParent && (
              <Button
                variant="ghost"
                size="icon"
                onClick={onMinimize}
                className="h-8 w-8 hover:bg-white/20"
                style={{ color: resolvedTheme.headerTextColor }}
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
            )}

            {/* React Native: Agent name centered */}
            <div className="flex items-center gap-2 absolute left-1/2 -translate-x-1/2">
              <span className="font-semibold">{resolvedTheme.headerText}</span>
            </div>
          </>
        ) : (
          <>
            {/* Web: Icon + name on left */}
            <div className="flex items-center gap-3">
              {resolvedTheme.iconUrl ? (
                <img
                  src={resolvedTheme.iconUrl}
                  alt={resolvedTheme.headerText}
                  className="h-8 w-8 rounded-full"
                />
              ) : (
                <div
                  className="h-8 w-8 rounded-full flex items-center justify-center"
                  style={{
                    ...getBackgroundStyle(resolvedTheme.accentColor),
                    color: resolvedTheme.textOverAccentColor
                  }}
                >
                  <span className="font-bold text-sm">
                    {resolvedTheme.headerText.charAt(0).toUpperCase()}
                  </span>
                </div>
              )}
              <span className="font-semibold">{resolvedTheme.headerText}</span>
            </div>
          </>
        )}

        {/* Right side: Menu options (both web and mobile) */}
        <div className="flex items-center gap-1 data-[react-native=true]:relative data-[react-native=true]:z-10">
          {!isReactNative && !useCustomParent && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onMinimize}
                  className="h-8 w-8 hover:bg-white/20"
                  style={{ color: resolvedTheme.headerTextColor }}
                >
                  <Minus className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Minimize</p>
              </TooltipContent>
            </Tooltip>
          )}
          {userToken ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 hover:bg-white/20"
                  style={{ color: resolvedTheme.headerTextColor }}
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={handleStartNewChat}>
                  <MessageCirclePlus className="h-4 w-4" />
                  <span className="ml-2">Start a new chat</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleEndChat} disabled={isChatEnded}>
                  <MessageCircleX className="h-4 w-4" />
                  <span className="ml-2">End chat</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onViewList}>
                  <History className="h-4 w-4" />
                  <span className="ml-2">View recent chats</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleEndChat}
                  disabled={isChatEnded}
                  className="h-8 w-8 hover:bg-white/20 disabled:opacity-40"
                  style={{ color: resolvedTheme.headerTextColor }}
                >
                  <X className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>End chat</p>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      </div>

      {/* Scrollable content area */}
      <div ref={scrollRef} className="overflow-y-auto flex-1 p-4 space-y-4 bg-white dark:bg-gray-950">
        {resolvedTheme.disclaimerText && (
          <div className="text-sm text-gray-500 dark:text-gray-400 text-center py-12 px-4">
            {resolvedTheme.iconUrl && (
              <img
                src={resolvedTheme.iconUrl}
                alt={resolvedTheme.headerText}
                className="w-16 h-16 rounded-full mx-auto mb-4"
              />
            )}
            <p className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
              {resolvedTheme.headerText}
            </p>
            <div
              className="disclaimer-content"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(resolvedTheme.disclaimerText) }}
            />
          </div>
        )}
        {(chatLoading || isStartingChat) && (
          <div className="flex items-start justify-start">
            <TypingIndicator />
          </div>
        )}
        {!chatLoading && !isStartingChat && messages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full text-center text-gray-500 dark:text-gray-400">
            <Bot className="h-12 w-12 mb-4 text-gray-300 dark:text-gray-600" />
            <p className="text-sm">Start a conversation with your AI assistant</p>
          </div>
        )}
        {!chatLoading && !isStartingChat && messages.length > 0 && (
          <>
            <ChatConversation
              messages={messages}
              removeUserOptions={removeUserOptions}
              agentName={resolvedTheme.agentName}
              theme={resolvedTheme}
              chatId={chatId}
              endedAt={
                existingChat?.chat?.terminatedAt ||
                existingChat?.chat?.abandonedAt ||
                existingChat?.chat?.handoffClosedAt
              }
              csatIsSatisfied={existingChat?.chat?.csatIsSatisfied}
              onCsatRatingChange={(isSatisfied) => updateCsatRating({ isSatisfied, organizationId, agentId })}
              componentDismissed={componentDismissed}
            />
            {isPending && chat?.status !== 'handedOff' && <TypingIndicator />}
          </>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Footer with input and powered by */}
      <div className="flex-shrink-0 p-3 bg-white dark:bg-gray-950 border-t border-gray-100 dark:border-gray-800 sm:rounded-b-2xl">
        {chat && !chat.terminatedAt && !chat.abandonedAt && !chat.handoffClosedAt ? (
          <>
            {userOptions.length > 0 && !isPending && !activeComponent && (
              <div className="relative mb-4">
                <div
                  ref={optionsScrollContainerRef}
                  className="overflow-y-auto"
                  style={optionsMaxHeight !== undefined ? { maxHeight: `${optionsMaxHeight}px` } : undefined}
                  onScroll={(e) => {
                    const { scrollHeight, scrollTop, clientHeight } = e.currentTarget;
                    setOptionsScrolledToBottom(scrollHeight - scrollTop - clientHeight < 2);
                  }}
                >
                  <div className="flex flex-col gap-2">
                    {userOptions.map((option, index) => (
                      <Button
                        key={index}
                        variant="outline"
                        size="sm"
                        onClick={() => handleOptionSelect(option)}
                        className="text-xs bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-100 text-center justify-center h-auto py-2 px-3 rounded-full whitespace-normal break-words min-w-0 max-w-full shadow-sm"
                        disabled={isPending}
                      >
                        {option.text}
                      </Button>
                    ))}
                  </div>
                </div>
                {!optionsScrolledToBottom && (
                  <div className="pointer-events-none absolute bottom-0 left-0 right-0 flex flex-col items-center">
                    <div className="h-10 w-full bg-gradient-to-t from-white dark:from-gray-950 to-transparent" />
                    <ChevronDown className="absolute bottom-0 h-4 w-4 animate-bounce text-gray-400" />
                  </div>
                )}
              </div>
            )}
            {activeComponent && !isPending && (
              <InteractiveComponentRenderer
                component={activeComponent}
                onSubmit={handleComponentSubmit}
                onDismiss={() => setComponentDismissed(true)}
                accentColor={resolvedTheme.accentColor}
                textOverAccentColor={resolvedTheme.textOverAccentColor}
                chatId={chatId}
              />
            )}
            {(!isStrictMode || isPending || componentDismissed) && !activeComponent && (
              <form onSubmit={handleSubmit} className="relative">
                {inputValue.length > MAX_MESSAGE_LENGTH && (
                  <div className="mb-2 text-xs text-right">
                    <span className="text-red-500">Message too long</span>
                  </div>
                )}
                {fileError && <p className="mb-1 text-xs text-red-500">{fileError}</p>}
                {selectedFiles.length > 0 && (
                  <div className="mb-2 flex flex-wrap gap-1.5">
                    {selectedFiles.map((file, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-1 bg-gray-100 dark:bg-gray-800 rounded-full px-2.5 py-1 text-xs text-gray-700 dark:text-gray-300"
                      >
                        <span className="max-w-[120px] truncate">{file.name}</span>
                        <button
                          type="button"
                          onClick={() => removeFile(index)}
                          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 flex-shrink-0"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/jpeg,image/png,image/gif,image/webp,application/pdf,.doc,.docx,.xls,.xlsx,.csv,.txt"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <div className="relative bg-gray-50 dark:bg-gray-900 rounded-full border border-gray-200 dark:border-gray-700 focus-within:border-gray-300 dark:focus-within:border-gray-500 transition-colors">
                  {chat?.handedOffTo === 'zendesk' && (
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="absolute left-2 top-1/2 -translate-y-1/2 p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                    >
                      <Paperclip className="h-4 w-4" />
                    </button>
                  )}
                  <input
                    ref={inputRef}
                    type="text"
                    placeholder={resolvedTheme.messagePlaceholder}
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={(e: React.KeyboardEvent) => {
                      if (
                        e.key === 'Enter' &&
                        !e.shiftKey &&
                        !isPending &&
                        inputValue.length <= MAX_MESSAGE_LENGTH
                      ) {
                        e.preventDefault();
                        handleSubmit(e as unknown as React.FormEvent);
                      }
                    }}
                    className={cn(
                      'w-full bg-transparent py-2.5 pr-11 text-base text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-400 focus:outline-none rounded-full',
                      chat?.handedOffTo === 'zendesk' ? 'pl-10' : 'pl-4'
                    )}
                  />
                  <div className="absolute right-2 top-1/2 -translate-y-1/2">
                    <Button
                      type="submit"
                      size="icon"
                      className="h-8 w-8 hover:opacity-90 rounded-full"
                      style={getBackgroundStyle(resolvedTheme.accentColor)}
                      disabled={
                        (!inputValue.trim() && selectedFiles.length === 0) ||
                        isPending ||
                        inputValue.length > MAX_MESSAGE_LENGTH
                      }
                      onPointerDown={(e) => e.preventDefault()}
                    >
                      {isPending && chat?.status !== 'handedOff' ? (
                        <Loader2
                          className="h-4 w-4 animate-spin"
                          style={{ color: resolvedTheme.textOverAccentColor }}
                        />
                      ) : (
                        <Send className="h-4 w-4" style={{ color: resolvedTheme.textOverAccentColor }} />
                      )}
                    </Button>
                  </div>
                </div>
              </form>
            )}
          </>
        ) : (
          chat &&
          !chat.abandonedAt && (
            <div className="flex flex-col items-center justify-center py-3 gap-2 text-center">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {chat.handoffClosedAt
                  ? 'Your conversation with support has ended.'
                  : 'The conversation was ended'}
              </p>
              <Button
                onClick={() => onNewChat(true)}
                size="sm"
                className="hover:opacity-90"
                style={{
                  ...getBackgroundStyle(resolvedTheme.headerBackgroundColor),
                  color: resolvedTheme.headerTextColor
                }}
              >
                Start New Chat
              </Button>
            </div>
          )
        )}
        {!isReactNative && (
          <a
            href="https://www.getstrada.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-1 pt-3 opacity-70 hover:opacity-90 transition-opacity dark:text-gray-400"
          >
            <span className="text-xs">Powered by</span>
            <img src={stradaLogo} alt="Strada" className="h-4 dark:invert" />
          </a>
        )}
      </div>

      {/* End Chat Confirmation Drawer */}
      <Drawer open={endChatDrawerOpen !== null} onOpenChange={(open) => !open && setEndChatDrawerOpen(null)}>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle>End this conversation?</DrawerTitle>
          </DrawerHeader>
          <DrawerFooter>
            <Button onClick={confirmEndChat} variant="destructive" className="w-full">
              End Chat
            </Button>
            <DrawerClose asChild>
              <Button variant="outline" className="w-full">
                Cancel
              </Button>
            </DrawerClose>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </div>
  );
};

type ChatWidgetProps = {
  organizationId: string;
  agentId: string;
  agentVariables: Record<string, unknown>;
  isEmbedded: boolean;
  isWidgetInitialized: boolean;
  metadata?: Record<string, unknown>;
  secureMetadata?: Record<string, unknown>;
  defaultView?: 'chat' | 'list';
  safeAreaInsets?: {
    top: number;
    bottom: number;
    left: number;
    right: number;
  };
  keyboardHeight?: number;
  useCustomParent?: boolean;
  darkMode?: boolean;
  resetChatSignal?: number;
};

export const ChatWidget = ({
  organizationId,
  agentId,
  agentVariables,
  isEmbedded,
  isWidgetInitialized,
  metadata,
  secureMetadata,
  defaultView,
  safeAreaInsets,
  keyboardHeight,
  useCustomParent = false,
  darkMode = false,
  resetChatSignal = 0
}: ChatWidgetProps) => {
  const [viewMode, setViewMode] = useState<'list' | 'chat'>(defaultView || 'chat');
  const isReactNative = !!(window as Window & { ReactNativeWebView?: unknown }).ReactNativeWebView;

  const queryClient = useQueryClient();
  const { userToken, isLoading: isLoadingToken } = useUserToken();
  const { mutateAsync: startChat, isPending: isStartingChat, error: startChatError } = useStartChat();

  const onCreateChat = useCallback(async () => {
    const response = await startChat({
      organizationId,
      agentId,
      userToken,
      metadata,
      agentVariables: {
        ...agentVariables,
        widget_source: isReactNative ? 'react-native-sdk' : 'web-sdk'
      },
      secureMetadata
    });
    setViewMode('chat');
    return response.chatId;
  }, [
    startChat,
    organizationId,
    agentId,
    userToken,
    metadata,
    agentVariables,
    isReactNative,
    secureMetadata
  ]);

  const prevResetSignal = useRef(resetChatSignal);

  // Consolidated hook: manages context key generation + chat persistence
  const { chatId, setChatId, clearChatAndStorage, contextKey } = useContextBasedChat({
    organizationId,
    agentId,
    agentVariables,
    isEmbedded,
    isWidgetInitialized,
    userToken,
    isLoadingToken,
    onCreateChat
  });

  // Fetch theme from API (with agentId for agent-specific configurations)
  const { data: themeData, isLoading: isLoadingTheme } = useGetTheme(organizationId, agentId);
  const theme = mergeTheme(themeData);

  // Resolve theme colors based on dark mode
  const resolvedTheme = useMemo(() => resolveThemeForDarkMode(theme, darkMode), [theme, darkMode]);

  const startNewChat = useCallback(
    async (clearStorage = false) => {
      if (clearStorage && contextKey) clearChatAndStorage();
      const response = await startChat({
        organizationId,
        agentId,
        userToken,
        metadata,
        agentVariables: { ...agentVariables, widget_source: isReactNative ? 'react-native-sdk' : 'web-sdk' },
        secureMetadata
      });
      setChatId(response.chatId);
      setViewMode('chat');
    },
    [
      contextKey,
      clearChatAndStorage,
      isReactNative,
      startChat,
      organizationId,
      agentId,
      userToken,
      metadata,
      agentVariables,
      secureMetadata,
      setChatId
    ]
  );

  const { mutateAsync: endCurrentChat } = useEndChat(chatId ?? '');
  useEffect(() => {
    if (resetChatSignal === prevResetSignal.current) return;
    prevResetSignal.current = resetChatSignal;

    const resetChat = async () => {
      if (chatId) {
        try {
          await endCurrentChat();
        } catch {
          // Chat may already be ended
        }
      }
      queryClient.invalidateQueries({ queryKey: widgetKeys.theme(organizationId, agentId) });
      clearChatAndStorage();
    };

    resetChat();
  }, [resetChatSignal, chatId, endCurrentChat, clearChatAndStorage, queryClient, organizationId, agentId]);

  const handleChatSelect = useCallback(
    (selectedChatId: string) => {
      setChatId(selectedChatId);
      setViewMode('chat');
    },
    [setChatId]
  );

  const handleViewList = useCallback(() => {
    setViewMode('list');
  }, []);

  const handleMinimize = useCallback(() => {
    sdkBridge.sendEvent('strada:chat:minimize', {});
  }, []);

  // STRADA_WIDGET_OPENED ensures we're in chat view
  useEffect(() => {
    const handleWidgetOpened = () => setViewMode('chat');
    sdkBridge.on('STRADA_WIDGET_OPENED', handleWidgetOpened);
    return () => sdkBridge.off('STRADA_WIDGET_OPENED', handleWidgetOpened);
  }, []);

  return (
    <TooltipProvider delayDuration={200}>
      <div
        data-testid="chat-widget"
        className={
          useCustomParent
            ? 'fixed z-50 bg-white dark:bg-gray-950 shadow-2xl inset-0 w-full h-full sm:inset-0 sm:h-full sm:w-full sm:rounded-2xl'
            : 'fixed z-50 bg-white dark:bg-gray-950 shadow-2xl inset-0 h-[100svh] sm:inset-auto sm:bottom-4 sm:right-4 sm:h-[700px] sm:w-96 sm:rounded-2xl'
        }
        style={
          isReactNative && safeAreaInsets
            ? {
                paddingTop: `${safeAreaInsets.top}px`,
                paddingBottom: 0,
                paddingLeft: `${safeAreaInsets.left}px`,
                paddingRight: `${safeAreaInsets.right}px`
              }
            : undefined
        }
      >
        {isLoadingTheme ? (
          <div className="flex flex-col items-center justify-center h-full">
            <Loader2 className="h-8 w-8 animate-spin text-gray-500 dark:text-gray-400" />
            <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">Loading...</p>
          </div>
        ) : viewMode === 'list' ? (
          <div className="flex flex-col h-full">
            <div
              className={cn('flex items-center justify-between sm:rounded-t-2xl p-4')}
              style={{
                ...getBackgroundStyle(resolvedTheme.headerBackgroundColor),
                color: resolvedTheme.headerTextColor
              }}
            >
              {!useCustomParent ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={handleMinimize}
                      className="h-8 w-8 hover:bg-white/20"
                      style={{ color: resolvedTheme.headerTextColor }}
                    >
                      <Minus className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Minimize</p>
                  </TooltipContent>
                </Tooltip>
              ) : (
                <div className="w-8" />
              )}
              <h1 className="font-semibold text-sm">Recent chats</h1>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => startNewChat()}
                    className="h-8 w-8 hover:bg-white/20"
                    style={{ color: resolvedTheme.headerTextColor }}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Start new chat</p>
                </TooltipContent>
              </Tooltip>
            </div>
            <div className="flex-1 overflow-y-auto min-h-0 bg-white dark:bg-gray-950">
              <RecentChats
                organizationId={organizationId}
                theme={resolvedTheme}
                onChatSelect={handleChatSelect}
              />
            </div>
          </div>
        ) : chatId ? (
          <ChatInterface
            key={chatId}
            metadata={metadata || {}}
            organizationId={organizationId}
            agentId={agentId}
            chatId={chatId}
            isStartingChat={isStartingChat}
            userToken={userToken}
            theme={theme}
            isReactNative={isReactNative}
            safeAreaInsets={safeAreaInsets}
            defaultView={viewMode}
            onNewChat={startNewChat}
            onViewList={handleViewList}
            onMinimize={handleMinimize}
            useCustomParent={useCustomParent}
            externalKeyboardHeight={keyboardHeight}
            darkMode={darkMode}
          />
        ) : startChatError ? (
          <div className="flex flex-col items-center justify-center h-full px-6 text-center">
            <p className="text-sm text-gray-500 dark:text-gray-400">{startChatError.message}</p>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-full">
            <Loader2 className="h-8 w-8 animate-spin text-gray-500 dark:text-gray-400" />
            <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">Starting chat...</p>
          </div>
        )}
      </div>
    </TooltipProvider>
  );
};

type RecentChatsProps = {
  organizationId: string;
  theme: WidgetTheme;
  onChatSelect: (chatId: string) => void;
};

const RecentChats = ({ organizationId, theme, onChatSelect }: RecentChatsProps) => {
  const { userToken } = useUserToken();
  const { data: history, isLoading } = useGetHistory(organizationId, userToken);

  const getStatusColor = (status: string) => {
    return status === 'open'
      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
      : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
  };

  const formatTimeAgo = (timestamp: string) => {
    const now = Date.now();
    const createdTime = new Date(timestamp).getTime();
    const diff = now - createdTime;
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);
    if (days > 0) {
      return `${days} day${days > 1 ? 's' : ''} ago`;
    }
    if (hours > 0) {
      return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    }
    if (minutes > 2) {
      return `${minutes} minutes ago`;
    }
    return 'Just now';
  };

  if (isLoading) {
    return (
      <div className="p-4">
        <div className="text-sm text-gray-500 dark:text-gray-400">Loading recent chats...</div>
      </div>
    );
  }

  if (!history?.chats?.length) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-center gap-4">
        <div
          className="h-16 w-16 rounded-full flex items-center justify-center"
          style={getBackgroundStyle(theme.accentColor)}
        >
          <MessageCircle className="h-8 w-8" style={{ color: theme.textOverAccentColor }} />
        </div>
        <div className="space-y-2">
          <h3 className="font-semibold text-lg text-gray-900 dark:text-gray-100">No Recent Chats</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm">
            Your chat history will appear here
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2 p-4">
      {history.chats.map((chat) => {
        const timeAgo = formatTimeAgo(chat.createdAt);
        const status = chat.status === 'terminated' || chat.status === 'abandoned' ? 'closed' : 'open';
        return (
          <div key={chat.chatId}>
            <div
              className="flex items-center gap-3 p-3 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg cursor-pointer transition-colors"
              onClick={() => onChatSelect(chat.chatId)}
            >
              <div
                className="h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0"
                style={getBackgroundStyle(theme.accentColor)}
              >
                <MessageCircle className="h-4 w-4" style={{ color: theme.textOverAccentColor }} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm text-gray-900 dark:text-gray-100 truncate">
                  {chat.summary || `Chat ${chat.chatId.slice(0, 8)}`}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                  <span>•</span>
                  <span>{timeAgo}</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={cn('px-2 py-1 rounded-full text-xs font-medium', getStatusColor(status))}>
                  {status === 'open' ? 'Open' : 'Closed'}
                </span>
                <svg
                  className="h-4 w-4 text-gray-500 dark:text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
